<template>
  <b-card>
    <div>
      <b-button
        id="default-filter-new-btn"
        variant="primary"
        type="button"
        size="sm"
        class="mb-2"
        @click="createRoles()"
      >
        <feather-icon icon="PlusIcon" size="15" />
        <span>{{ t('Add New Role') }}</span>
        <b-tooltip
          triggers="hover"
          target="default-filter-new-btn"
          :title="t('Add New Role')"
          :delay="{ show: 0, hide: 50 }"
        />
      </b-button>
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-if="props.column.field === 'description'">
            {{ props.row.description }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="getDetailRoles(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="t('Edit Role')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-roles-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="getDeleteRoles(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-roles-button-${props.row.id}`"
                  :title="t('Delete Role')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-dropdown size="sm" variant="secondary" right dropup stop>
                <template #button-content>
                  <feather-icon icon="settings" size="16" class="mr-1" />
                  <b>{{ t('Others') }}</b>
                </template>
                <b-dropdown-item @click="getDetailPermissions(props.row.id)">
                  {{ t('Edit Permissions') }}
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </span>
        </template>
      </vue-good-table>
    </div>
  </b-card>
</template>
<script>
import { BBadge, BButton, BCard, BTooltip, BDropdown, BDropdownItem } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BTooltip,
    BDropdown,
    BDropdownItem,
    VueGoodTable
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      values: {
        status: false
      }
    };
  },

  computed: {
    ...mapGetters({
      allRoles: 'app/allRoles'
    }),
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Role'),
          field: 'name'
        },
        {
          label: this.$t('Description'),
          field: 'description'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async created() {
    this.loadItems();
  },

  methods: {
    createRoles() {
      this.$router.push('/roles/create');
    },
    getDetailRoles(id) {
      this.$router.push(`/roles/${id}`);
    },
    getDetailPermissions(id) {
      this.$router.push(`/roles/${id}/permissions`);
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    async deleteRoles(id) {
      return useService.deleteRoles(id);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData
      };

      this.isLoading = true;

      useService
        .getRoles(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getDeleteRoles(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Role Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteRoles(id)
            .then(response => {
              this.showSuccessMessage(`${response.message || this.$t('Role Was Deleted')}`);

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.package-card {
  background-color: #4f4f4f80 !important;
  margin: 24px;
}

.checkbox {
  width: 100%;
  margin-left: 5px;
}

.provider-list {
  background-color: transparent;
  padding: 15px;
}

.provider-item {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding: 10px 0;
}

.provider-info {
  display: flex;
  align-items: center;
}

.provider-logo {
  max-width: 50px;
  max-height: 50px;
  margin-right: 10px;
}

.provider-name {
  flex: 1;
  margin-left: 50px;
  font-weight: bold;
  font-size: 14px;
}

.provider-item .b-btn-outline-primary {
  border-color: #007bff;
  width: 150px;
}
</style>
